// One definition of "how many bets does this search have", shared by the
// results page (All Books / My Books toggle, "X of Y Bets") and the Background
// Queries card. Before Sep 30, 2026 the card counted unique cards with its own
// min-books rule (exchange/offshore keys counted toward the floor, substring
// My Books matching, a different My Books pool), so "158 · 132" on the card
// became "152 · 106" on the page.
//
// countResults runs the results page's own filter pipeline with the filters a
// fresh search starts with, in both book modes, and returns item counts.
import { computeFilteredAndSortedOptions } from '../hooks/useFilteredAndSortedOptions';
import { makeSortEvForBook, makePreferredBook } from './resultPipelineHelpers';
import { resolveConsensusOptionsFromPrefs } from './useConsensusOptions';
import { normalizeQuery, getMarketRelevanceToQuery, _extractNamesFromSelection, _isOptionPlayerProp } from './sgpFlowUtils';
import { countCards } from './cardGrouping';
import { explainFilterDrop, formatFilterDropSubtitle } from './filterStageExplain';

/**
 * Counts the Background Queries card shows, plus whether the results page will
 * open with the min-books floor relaxed (everything below the floor but raw > 0),
 * in which case the counts are the relaxed ones the page will show.
 */
export function countBackgroundQuery(query, userProfile, { hydrate = null } = {}) {
    // Count what the page will render: the hand-off hydrates results first
    // (dropping unrenderable cached SGPs), so the counts do too.
    const prep = (list) => (hydrate ? hydrate(list || []).filter(Boolean) : (list || []));
    const base = {
        results: prep(query.results),
        myBooksResults: prep(query.myBooksResults),
        prompt: query.prompt || '',
        queryClassification: query._queryClassification || null,
        referenceTime: query.referenceTime || null,
        preferredSportsbook: query._preferredSportsbook || null,
        userProfile,
    };
    const strict = countResults(base);
    if (strict.allBooksCount > 0 || base.preferredSportsbook || !base.results.length) {
        return { ...strict, relaxMinBooks: false };
    }
    return { ...countResults({ ...base, relaxMinBooks: true }), relaxMinBooks: true };
}

export function countResults({
    results = [],
    myBooksResults = [],
    prompt = '',
    queryClassification = null,
    referenceTime = null,
    preferredSportsbook = null,
    relaxMinBooks = false,
    userProfile = null,
}) {
    const prefs = userProfile?.preferences || {};
    const myBooksList = Array.isArray(prefs.myBooks) ? prefs.myBooks.filter(Boolean) : [];
    const consensusOptions = resolveConsensusOptionsFromPrefs(prefs);
    const sortEvForBook = makeSortEvForBook(consensusOptions);
    const effectiveBooksSort = prefs.defaultBookSort || 'ev';
    // Like the page: one preferred-book picker for both runs, from the default books view.
    const pageWantsMyBooks = myBooksList.length > 0 && effectiveBooksSort === 'mybooks';
    const getMyBooksBest = makePreferredBook({ wantMyBooks: pageWantsMyBooks, myBooksList, sortEvForBook });

    const run = (wantMyBooks) => computeFilteredAndSortedOptions({
        fetchedOptions: results,
        myBooksFetchedOptions: myBooksResults,
        resultsContextQuery: prompt,
        responseMetadata: queryClassification ? { _queryClassification: queryClassification } : null,
        referenceTime,
        didBroaden: false,
        preferredSportsbook,
        searchText: '',
        filterSportsbooks: preferredSportsbook ? [preferredSportsbook] : [],
        filterMarkets: [],
        filterPlayers: [],
        filterResultType: 'all',
        filterDeepLinksOnly: false,
        filterPositiveEvOnly: !!prefs.filterPositiveEvOnly,
        filterLineType: 'all',
        filterLegCount: null,
        disableMinBooksFilter: !!relaxMinBooks,
        disableMyBooksPriority: false,
        sortBy: 'ev',
        _effectiveBooksSort: effectiveBooksSort,
        _wantsMyBooksMode: wantMyBooks,
        _myBooksList: myBooksList,
        userProfile,
        consensusOptions,
        _extractNamesFromSelection,
        _getMyBooksBest: getMyBooksBest,
        _isOptionPlayerProp,
        _sortEvForBook: sortEvForBook,
        getMarketRelevanceToQuery,
        normalizeQuery,
    });

    const allBooks = run(false);
    const myBooks = myBooksList.length > 0 ? run(true) : [];
    return {
        allBooksCount: allBooks.length,
        myBooksCount: myBooks.length,
        allBooksCards: countCards(allBooks),
        myBooksCards: countCards(myBooks),
        rawResultCount: results.length,
        hasMyBooks: myBooksList.length > 0,
        // Why lines are hidden in All Books (null when none are).
        hiddenText: formatFilterDropSubtitle(explainFilterDrop(allBooks.filterStageStats),
            parseInt(prefs.minBooksStraights, 10) || 5),
        unreliableOnlyBooks: allBooks.unreliableOnlyBooks || null,
    };
}

/**
 * The one wording every results count uses (Oct 1, 2026 — the Background
 * Queries card, the results header and the book toggle used to show different
 * units: raw lines, filtered lines, unique-selection "cards").
 *   lines  bets shown after filters (each line/price is one bet)
 *   cards  cards on screen (utils/cardGrouping): a card holds a team/player's
 *          lines for one market, both sides
 */
export function describeResultCounts({ lines = 0, cards = null, sgp = false }) {
    if (sgp) return `${lines} SGP${lines === 1 ? '' : 's'}`;
    const l = `${lines} line${lines === 1 ? '' : 's'}`;
    if (cards === null || cards === undefined || cards === lines) return l;
    return `${l} in ${cards} card${cards === 1 ? '' : 's'}`;
}
